import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';
import { Button } from '../components/ui/button';
import { Clock, ArrowRight, Calendar, MapPin, Users } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const Projects = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const upcomingProjects = [
    {
      title: 'Trans-National Oil Pipeline Inspection',
      description: 'Comprehensive inspection of 500km cross-border oil pipeline infrastructure.',
      status: 'In Progress',
      duration: '6 months',
      location: 'North America',
      team: '12 Engineers',
      completion: '45%',
    },
    {
      title: 'Metro Underground Tunnel Survey',
      description: 'Complete 3D mapping and safety assessment of urban metro tunnel network.',
      status: 'Planning',
      duration: '4 months',
      location: 'Asia Pacific',
      team: '8 Engineers',
      completion: '10%',
    },
    {
      title: 'Natural Gas Infrastructure Assessment',
      description: 'Autonomous inspection of critical gas pipeline networks across multiple sites.',
      status: 'Planning',
      duration: '8 months',
      location: 'Europe',
      team: '15 Engineers',
      completion: '5%',
    },
  ];

  return (
    <div className="min-h-screen bg-primary">
      <Header />
      <ScrollToTop />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute top-20 right-10 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#F97316' }} />
        <div className="absolute bottom-20 left-10 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#35474B' }} />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-foreground-primary mb-6">Our Projects</h1>
          <p className="text-xl text-foreground-secondary">
            Pioneering the future of autonomous industrial inspection
          </p>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="glass-frosted rounded-3xl p-16 text-center glow-subtle">
            <div className="flex justify-center mb-8">
              <div
                className="w-24 h-24 rounded-2xl flex items-center justify-center animate-pulse"
                style={{
                  background: 'rgba(249, 115, 22, 0.1)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                }}
              >
                <Clock size={48} style={{ color: '#F97316' }} />
              </div>
            </div>
            <h2 className="text-4xl font-bold text-foreground-primary mb-6">Detailed Case Studies Coming Soon</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-foreground-secondary">
              We're currently documenting our breakthrough projects and preparing comprehensive case studies. 
              These will showcase real-world applications, technical challenges overcome, and measurable results achieved.
            </p>
          </div>
        </div>
      </section>

      {/* Upcoming Projects */}
      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground-primary mb-4">Projects in Pipeline</h2>
            <p className="text-lg text-foreground-secondary">
              Get a glimpse of what we're working on
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {upcomingProjects.map((project, index) => (
              <div
                key={index}
                className="glass-frosted rounded-2xl p-8 transition-smooth glow-subtle group"
              >
                {/* Status Badge */}
                <div className="flex justify-between items-start mb-6">
                  <div
                    className="px-4 py-2 rounded-full text-sm font-semibold"
                    style={{
                      background: project.status === 'In Progress' ? 'rgba(249, 115, 22, 0.2)' : isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.1)',
                      color: project.status === 'In Progress' ? '#F97316' : undefined,
                    }}
                  >
                    {project.status}
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-foreground-primary mb-4">{project.title}</h3>
                <p className="text-sm mb-6 leading-relaxed text-foreground-secondary">
                  {project.description}
                </p>

                {/* Project Details */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <Calendar size={16} style={{ color: '#F97316' }} />
                    <span className="text-sm text-foreground-secondary">
                      Duration: {project.duration}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin size={16} style={{ color: '#F97316' }} />
                    <span className="text-sm text-foreground-secondary">
                      {project.location}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users size={16} style={{ color: '#F97316' }} />
                    <span className="text-sm text-foreground-secondary">
                      {project.team}
                    </span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-foreground-secondary">
                      Progress
                    </span>
                    <span className="text-xs font-semibold" style={{ color: '#F97316' }}>
                      {project.completion}
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full" style={{ background: isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)' }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: project.completion,
                        background: 'linear-gradient(90deg, #F97316 0%, #A64425 100%)',
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Button
              size="lg"
              className="px-8 py-6 text-lg font-semibold transition-smooth group"
              style={{
                background: '#F97316',
                color: '#FFFFFF',
              }}
              onClick={() => navigate('/contact')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 0 40px rgba(249, 115, 22, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Discuss Your Project
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Projects;
